import { useState, useId, ChangeEvent } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { ReactComponent as EyeIcon } from "../../../assets/eye.icon.svg";
import { ReactComponent as EyeOffIcon } from "../../../assets/eye-off.icon.svg";
import { ReactComponent as GoogleIcon } from "../../../assets/google.icon.svg";
import { ReactComponent as MicrosoftIcon } from "../../../assets/microsoft.icon.svg";
import { AuthErrorMessage } from "../../../common/enums/auth-error-message.enum";
import { FirebaseErrorCode } from "../../../common/enums/firebase-error-code.enum";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { emailRegex } from "../../../common/utilities/constants/constants";
import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { Loader, LoaderView } from "../../../components/loader/loader.comp";

const FirebaseSignUp = () => {
    const emailMask = new RegExp(process.env.REACT_APP_EMAIL_REGEX ?? emailRegex, "g");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const invite = searchParams.get("invite");
    const loginLink = !invite ? "/login" : `/login?invite=${invite}`;
    const { signUp, signInWithGoogle, signInWithMicrosoft, providerLoading } =
        FirebaseAuthContainer.useContainer();
    const [login, setLogin] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [passwordType, setPasswordType] = useState<string>("password");
    const [confirmPasswordType, setConfirmPasswordType] = useState<string>("password");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInvalid, setIsInvalid] = useState<boolean>(false);
    const errorId = useId();

    const handleLoginChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setLogin(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setPassword(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handleConfirmPasswordChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setConfirmPassword(val ?? "");
        setErrorMessage("");
        setIsInvalid(false);
    };

    const handlePasswordEyeClick = () => {
        setPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    const handleConfirmPasswordEyeClick = () => {
        setConfirmPasswordType(prevValue => (prevValue === "text" ? "password" : "text"));
    };

    const handleSignUnClick = () => {
        if (!login || !password || !confirmPassword) return;

        if (!login.match(emailMask)) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.invalidEmail);
            return;
        } else if (password !== confirmPassword) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.differentPasswords);
            return;
        } else if (!invite) {
            setIsInvalid(true);
            setErrorMessage(AuthErrorMessage.invalidInvite);
            return;
        }

        setIsLoading(true);
        signUp(login, password, invite)
            .catch(error => {
                switch (error.code as FirebaseErrorCode) {
                    case FirebaseErrorCode.emailAlreadyInUse:
                        setIsInvalid(true);
                        setErrorMessage(AuthErrorMessage.emailAlreadyInUse);
                        return;
                    case FirebaseErrorCode.weakPassword:
                        setIsInvalid(true);
                        setErrorMessage(AuthErrorMessage.weakPassword);
                        return;
                    case FirebaseErrorCode.invalidInvite:
                        setIsInvalid(true);
                        setErrorMessage(AuthErrorMessage.invalidInvite);
                        return;
                    case FirebaseErrorCode.inappropriateInvite:
                        setIsInvalid(true);
                        setErrorMessage(AuthErrorMessage.inappropriateInvite);
                        return;
                    default:
                        setErrorMessage(AuthErrorMessage.default);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleSignInWithGoogleClick = () => {
        if (isLoading) return;

        signInWithGoogle(invite).catch(error => {
            providerErrorHandler(error);
        });
    };

    const handleSignInWithMicrosoftClick = () => {
        if (isLoading) return;

        signInWithMicrosoft(invite).catch(error => {
            providerErrorHandler(error);
        });
    };

    const providerErrorHandler = (error: any) => {
        switch (error.code as FirebaseErrorCode) {
            case FirebaseErrorCode.closePopup:
            case FirebaseErrorCode.cancelPopup:
                break;
            case FirebaseErrorCode.invalidInvite:
                setErrorMessage(AuthErrorMessage.invalidInvite);
                return;
            case FirebaseErrorCode.inappropriateInvite:
                setErrorMessage(AuthErrorMessage.inappropriateInvite);
                return;
            case FirebaseErrorCode.invalidEmail:
                setErrorMessage(AuthErrorMessage.invalidEmail);
                return;
            case FirebaseErrorCode.forbiddenInvite:
                setErrorMessage(AuthErrorMessage.forbiddenInvite);
                return;
            case FirebaseErrorCode.invalidProvider:
                setErrorMessage(AuthErrorMessage.invalidProvider);
                return;
            case FirebaseErrorCode.accessPermissions:
                navigate(`/error?title=403 Error&description=${AuthErrorMessage.lackPermissions}`);
                return;
            default:
                setErrorMessage(AuthErrorMessage.default);
        }
    };

    return (
        <AuthTemplate>
            <div className="signup">
                <div className="signup-title" role="heading" aria-level={1}>
                    Sign Up
                </div>
                <div className="signup-description">
                    <span className="signup-description__text">Already have an account?</span>
                    <Link to={loginLink} className="signup-description__link">
                        Log In
                    </Link>
                </div>
                <div className="signup-content">
                    <div className="signup-field">
                        <input
                            className="signup-field__input--required"
                            type="email"
                            aria-label="Email"
                            autoComplete="email"
                            required
                            aria-invalid={errorMessage ? true : undefined}
                            aria-describedby={errorMessage ? errorId : undefined}
                            placeholder="Email"
                            onChange={handleLoginChange}
                            value={login}
                        />
                        <div className="signup-field-settings">
                            <span className="signup-field-settings__required" aria-hidden="true">
                                *
                            </span>
                        </div>
                    </div>
                    <div className="signup-field">
                        <input
                            className="signup-field__input--eye-required"
                            type={passwordType}
                            aria-label="Password"
                            autoComplete="new-password"
                            required
                            aria-invalid={errorMessage ? true : undefined}
                            aria-describedby={errorMessage ? errorId : undefined}
                            placeholder="Password"
                            onChange={handlePasswordChange}
                            value={password}
                        />
                        <div className="signup-field-settings">
                            <button
                                type="button"
                                className="signup-field-settings__eye"
                                aria-label={
                                    passwordType === "text" ? "Hide password" : "Show password"
                                }
                                aria-pressed={passwordType === "text"}
                                onClick={handlePasswordEyeClick}
                            >
                                <SvgIcon
                                    component={passwordType === "text" ? EyeIcon : EyeOffIcon}
                                    inheritViewBox
                                    aria-hidden="true"
                                />
                            </button>
                            <span className="signup-field-settings__required" aria-hidden="true">
                                *
                            </span>
                        </div>
                    </div>
                    <div className="signup-field">
                        <input
                            className="signup-field__input--eye-required"
                            type={confirmPasswordType}
                            aria-label="Confirm Password"
                            autoComplete="new-password"
                            required
                            aria-invalid={errorMessage ? true : undefined}
                            aria-describedby={errorMessage ? errorId : undefined}
                            placeholder="Confirm Password"
                            onChange={handleConfirmPasswordChange}
                            value={confirmPassword}
                        />
                        <div className="signup-field-settings">
                            <button
                                type="button"
                                className="signup-field-settings__eye"
                                aria-label={
                                    confirmPasswordType === "text"
                                        ? "Hide password"
                                        : "Show password"
                                }
                                aria-pressed={confirmPasswordType === "text"}
                                onClick={handleConfirmPasswordEyeClick}
                            >
                                <SvgIcon
                                    component={
                                        confirmPasswordType === "text" ? EyeIcon : EyeOffIcon
                                    }
                                    inheritViewBox
                                    aria-hidden="true"
                                />
                            </button>
                            <span className="signup-field-settings__required" aria-hidden="true">
                                *
                            </span>
                        </div>
                    </div>
                    <div className="signup-message">
                        <span id={errorId} role="alert">
                            {errorMessage}
                        </span>
                    </div>
                    <div className="signin-separator"></div>
                    <div className="signin-providers">
                        <Button
                            className="signin-providers__button"
                            variant="outlined"
                            onClick={handleSignInWithGoogleClick}
                            startIcon={<SvgIcon component={GoogleIcon} inheritViewBox />}
                        >
                            Continue with Google
                        </Button>
                        <Button
                            className="signin-providers__button"
                            variant="outlined"
                            onClick={handleSignInWithMicrosoftClick}
                            startIcon={<SvgIcon component={MicrosoftIcon} inheritViewBox />}
                        >
                            Continue with Microsoft
                        </Button>
                    </div>
                </div>
                <div className="signup-action">
                    <Button
                        className="primary-button"
                        variant="text"
                        onClick={handleSignUnClick}
                        disabled={!login || !password || !confirmPassword || isLoading || isInvalid}
                    >
                        {!isLoading ? <span>Sign up</span> : <Loader size={24} borderWidth={4} />}
                    </Button>
                </div>
                <LoaderView show={providerLoading} />
            </div>
        </AuthTemplate>
    );
};

export default FirebaseSignUp;
